import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
} from "@exhousing/design-system";
import { CalendarCheck, Plus } from "lucide-react";
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { useCreateViewing, useViewings } from "@/hooks/api/useViewings";
import { useProperties } from "@/hooks/api/useProperties";

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function ViewingsPage() {
  const { data: viewings } = useViewings();
  const { data: properties } = useProperties();
  const create = useCreateViewing();
  const [searchParams, setSearchParams] = useSearchParams();
  const propertyFilter = searchParams.get("property") ?? "";
  const [showForm, setShowForm] = useState(false);
  const [propertyId, setPropertyId] = useState(propertyFilter);
  const [when, setWhen] = useState("");
  const [capacity, setCapacity] = useState("6");

  const onCreate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!propertyId || !when) return;
    create.mutate(
      {
        property_id: propertyId,
        scheduled_at: new Date(when).toISOString(),
        duration_minutes: 30,
        max_capacity: Math.max(1, Number(capacity) || 6),
      },
      {
        onSuccess: () => {
          toast.success("Viewing scheduled");
          setShowForm(false);
          setPropertyId("");
          setWhen("");
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  const rows = (viewings ?? []).filter(
    (v) => !propertyFilter || v.property_id === propertyFilter,
  );

  const propLabel = (id: string) => {
    const p = (properties ?? []).find((x) => x.id === id);
    return p
      ? [p.street, p.house_number].filter(Boolean).join(" ") || p.city || "Property"
      : "Property";
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl font-medium tracking-tight">Viewings</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {viewings ? `${viewings.length} scheduled` : "Loading…"}
          </p>
        </div>
        <Button onClick={() => setShowForm((s) => !s)}>
          <Plus className="size-4" />
          Schedule viewing
        </Button>
      </header>

      {propertyFilter && (
        <button
          type="button"
          onClick={() => setSearchParams({})}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
        >
          Filtered to {propLabel(propertyFilter)} · clear
        </button>
      )}

      {showForm && (
        <Card className="animate-fade-in-up">
          <CardContent className="pt-6">
            <form onSubmit={onCreate} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="property">Property</Label>
                <select
                  id="property"
                  value={propertyId}
                  onChange={(e) => setPropertyId(e.target.value)}
                  className="flex h-11 w-full rounded-md border border-input bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">Select a property…</option>
                  {(properties ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {propLabel(p.id)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="when">Date &amp; time</Label>
                <Input
                  id="when"
                  type="datetime-local"
                  value={when}
                  onChange={(e) => setWhen(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="capacity">Max candidates</Label>
                <Input
                  id="capacity"
                  type="number"
                  min={1}
                  max={100}
                  value={capacity}
                  onChange={(e) => setCapacity(e.target.value)}
                />
              </div>
              <div className="flex gap-2 sm:col-span-2">
                <Button type="submit" disabled={create.isPending || !propertyId || !when}>
                  {create.isPending ? "Scheduling…" : "Schedule"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0">
          {rows.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="px-6 py-3 font-medium">When</th>
                  <th className="px-6 py-3 font-medium">Property</th>
                  <th className="px-6 py-3 font-medium tabular-nums">Signups</th>
                  <th className="px-6 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((v) => (
                  <tr key={v.id} className="border-b border-border/60 last:border-0">
                    <td className="px-6 py-3">
                      <Link
                        to={`/viewings/${v.id}`}
                        className="font-medium hover:text-primary"
                      >
                        {formatWhen(v.scheduled_at)}
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-muted-foreground">
                      {propLabel(v.property_id)}
                    </td>
                    <td className="px-6 py-3 tabular-nums">
                      {v.signup_count} / {v.max_capacity}
                    </td>
                    <td className="px-6 py-3">
                      <Badge tone={v.is_cancelled ? "danger" : "success"} dot>
                        {v.is_cancelled ? "Cancelled" : "Scheduled"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="flex flex-col items-center gap-3 py-16 text-center text-muted-foreground">
              <CalendarCheck className="size-8 opacity-40" />
              <p className="text-sm">No viewings scheduled.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
